import { useEffect, useState } from "react";
import styled from "styled-components";
import { ToursCategory, StyledBanner } from "@components";
import { AppModuleLayout } from "@layouts";
import { SearchModule } from "@module";
import { breakpoint, theme } from "@utilities";
import {
    getMetaData,
    loadAccountDetail,
    selectAccount,
    selectAuth,
    selectCmsHomepage,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import _ from "lodash";
import { fetchPoints } from "@redux";
import { PageHead, SectionBlockRenderer } from "@components";

interface HomeProps {
    // When false (the live `/` route) the homepage always renders the original
    // banner and product carousels — CMS content NEVER overrides it. The
    // CMS-driven homepage is only shown when true (the `/cms/home` preview),
    // so the live site stays exactly as-is until the CMS is fully converted.
    useCms?: boolean;
}

export const Home = ({ useCms = false }: HomeProps) => {
    //page state
    const dispatch = useAppDispatch();
    const [isIncreasePaddingTop, setIsIncreasePaddingTop] = useState(false);
    const { jwt } = useAppSelector(selectAuth);
    const { account } = useAppSelector(selectAccount);
    const [isSignedIn, setIsSignedIn] = useState(!_.isNil(jwt) && !_.isNil(account));
    const cmsHomepageState = useAppSelector(selectCmsHomepage);
    const cmsHomepage = useCms ? cmsHomepageState : null;

    useEffect(() => {
        setIsSignedIn(!_.isNil(jwt) && !_.isNil(account));
    }, [jwt, account]);

    useEffect(() => {
        if (isSignedIn && account?.is_agent_integrated) {
            dispatch(fetchPoints());
            dispatch(loadAccountDetail());
            dispatch(getMetaData());
        }
    }, [dispatch, isSignedIn, account?.is_agent_integrated]);

    const handleChangeIsIncreasePaddingTop = () => {
        setIsIncreasePaddingTop(!isIncreasePaddingTop);
    };

    return (
        <AppModuleLayout>
            <Container>
                <PageHead
                    seo={
                        cmsHomepage?.seo ?? {
                            title: theme.title,
                            metaDescription: `Browse and book tours, trips and travel experiences with ${theme.title}.`,
                            structuredDataType: "WebPage",
                        }
                    }
                />

                {cmsHomepage?.hero ? (
                    <SectionBlockRenderer sections={[cmsHomepage.hero]} />
                ) : (
                    <StyledBanner>
                        <div className="bannerMobile">
                            <img
                                src="/images/banner/Banner-home-50.png"
                                alt="Travel Mobile Banner"
                            />
                            <h2 className="heading">
                                Travel & live your <br /> best life
                            </h2>
                        </div>
                        <div className="bannerDesktop">
                            <img
                                src="/images/website/banner/banner.png"
                                alt="Travel Desktop Banner"
                            />
                            <h2 className="heading">Travel & live your best life</h2>
                        </div>
                    </StyledBanner>
                )}
                <div className="search">
                    <SearchModule onChangeIsIncreasePaddingTop={handleChangeIsIncreasePaddingTop} />
                </div>
                {/* we may need this code for future when we add favorites */}
                {/* {favoritesTour?.length > 0 && (
                    <div className="listDestinations">
                        <h2 className="topDestinations">My Favourites</h2>
                        {favoritesTour.slice(0, 4).map((tour) => {
                            return <TourElementModule data={tour} key={tour.productId} />;
                        })}
                    </div>
                )} */}
                <ToursCategory />
                {cmsHomepage?.sections && cmsHomepage.sections.length > 0 ? (
                    <SectionBlockRenderer sections={cmsHomepage.sections} />
                ) : null}
                {/* we may need this code for future when we add favorites */}
                {/* {favoritesTour?.length > 0 && (
                    <div className="listDestinationsWebsite">
                        <h2 className="topDestinations">My Favourites</h2>
                        <PromoTourModule data={favoritesTour.slice(0, 4)} type="favorite" />
                    </div>
                )} */}
            </Container>
            {/* {isShowWelComePopup ? <WelcomePopupModule onClosePopup={handleClosePopup} /> : <></>} */}
        </AppModuleLayout>
    );
};

// TEMPORARY: CMS-driven homepage preview, mounted at `/cms/home`. Renders the
// backend-composed hero and content sections. The live `/` homepage stays on
// the original banner until the CMS is fully converted.
export const CmsHome = () => <Home useCms />;

const Container = styled.div`
    background-color: #fff;
    width: 100vw;
    padding-bottom: 60px;

    .search {
        position: relative;
        ${breakpoint.breakTablet`
            z-index: 3;
        `}/* ${breakpoint.breakOnlyMobile`
            z-index: 3;
        `} */
    }

    .category {
        padding: 0px 16px 0px 16px;
        ${breakpoint.breakOnlyMobile`
            padding: 0px 0px 0px 0px;
        `}

        /* Every card body in the section, whichever layout drew it — the desktop
           carousel and the mobile two-row grid render different wrappers around
           the same card. */
        .main {
            padding-left: 5px;
        }
    }

    .topDestinations {
        line-height: 36.46px;
        font-size: 28px;
        font-weight: 700;
        color: ${(props) => props.theme.colors.black};
        // margin-bottom: 20px;
    }

    @media (min-width: 1110px) {
        .topDestinations {
            &.topTour {
                margin-top: 80px;
            }
        }
    }
`;
